import React, { useState, useEffect } from 'react';
import { GeneralContractorService } from '../../services/general-contractor.service';
import { toast } from 'react-toastify';
import { authService } from '../../services/auth.service';
import { getGeneralContractorManagementAccess } from '../../config/roleAccess';
import StatusPill from '../StatusPill';

// Define interfaces for the API response
interface Province {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface Address {
  id: string;
  address: string;
  city: string;
  postalCode: string;
  coordinates: {
    type: string;
    coordinates: number[];
  } | null;
  createdAt: string;
  updatedAt: string | null;
  province: Province;
}

interface GeneralContractorResponse {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  address: Address | null;
}

interface FormattedGeneralContractor {
  id: string;
  name: string;
  active: boolean;
  status: string;
  address: Address | null;
  created_at: string;
  updated_at: string;
}

interface GeneralContractorDetailsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  generalContractorId: string;
  isEditMode: boolean;
  onEditClick: () => void;
  onSaveEdit: (options?: { toastShown?: boolean }) => void;
  editFormData: any;
  onEditInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> |
    { target: { name: string; value: any } }
  ) => void;
  onShowBlockDialog: () => void;
  setSelectedGeneralContractor?: (generalContractor: any) => void;
  setEditFormData?: (formData: any) => void;
  setShowAddGeneralContractorDialog: (show: boolean) => void;
}

const GeneralContractorDetailsOverlay: React.FC<GeneralContractorDetailsOverlayProps> = ({
  isOpen,
  onClose,
  generalContractorId,
  isEditMode,
  onEditClick,
  onSaveEdit,
  editFormData,
  onEditInputChange,
  onShowBlockDialog,
  setSelectedGeneralContractor,
  setEditFormData,
  setShowAddGeneralContractorDialog,
}) => {
  const [generalContractor, setGeneralContractor] = useState<FormattedGeneralContractor | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Get current user and access permissions
  const user = authService.getUser();
  const generalContractorAccess = getGeneralContractorManagementAccess(user?.role || '');

  // Fetch general contractor data when ID changes or component opens
  useEffect(() => {
    if (isOpen && generalContractorId) {
      setIsLoading(true);
      GeneralContractorService.getGeneralContractorById(generalContractorId)
        .then((response: any) => {
          const generalContractorResponse = response['data'] as GeneralContractorResponse;

          // Create a properly formatted general contractor object for details view
          const formattedGeneralContractor: FormattedGeneralContractor = {
            id: generalContractorResponse.id || '',
            name: generalContractorResponse.name || '',
            active: generalContractorResponse.active ?? true,
            status: generalContractorResponse.active ? 'Active' : 'Inactive',
            address: generalContractorResponse.address || null,
            created_at: generalContractorResponse.createdAt,
            updated_at: generalContractorResponse.updatedAt,
          };

          setGeneralContractor(formattedGeneralContractor);
          // Update parent state if function is provided
          if (setSelectedGeneralContractor) {
            setSelectedGeneralContractor(formattedGeneralContractor);
          }
          if (setEditFormData) {
            setEditFormData(formattedGeneralContractor);
          }
        })
        .catch(error => {
          console.error('Error fetching general contractor details:', error);
          toast.error('Failed to fetch general contractor details');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [generalContractorId, isOpen, setSelectedGeneralContractor, setEditFormData]);

  // Add useEffect to handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleEditButtonClick = () => {
    if (!generalContractorAccess.editGeneralContractor) {
      toast.error('You do not have permission to edit general contractors');
      return;
    }

    if (generalContractor && setEditFormData) {
      // Format the general contractor data for the form
      const formattedData = {
        id: generalContractor.id,
        name: generalContractor.name,
        active: generalContractor.active,
        address: generalContractor.address?.address || '',
        city: generalContractor.address?.city || '',
        province: generalContractor.address?.province?.id || '',
        postalCode: generalContractor.address?.postalCode || '',
        coordinates: generalContractor.address?.coordinates?.coordinates || null
      };
      setEditFormData(formattedData);
    }
    onClose(); // Close the details overlay
    setShowAddGeneralContractorDialog(true); // Open the dialog in edit mode
  };

  const handleSaveEdit = () => {
    onSaveEdit();
  };

  const handleBlockToggle = () => {
    onShowBlockDialog();
  };

  // Don't render anything if not open
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed inset-0 bg-[#A42823] transition-all duration-300 ease-in-out ${
          isOpen ? 'bg-black bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
        } z-50`}
        onClick={e => {
          // Only close if the click is on the overlay itself, not the side panel
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        data-testid="general-contractor-details-overlay-bg"
      >
        <div
          data-testid="general-contractor-details-overlay"
          className={`fixed right-0 bg-white h-screen w-full md:w-[500px] shadow-lg transition-all duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          } flex flex-col`}
        >
          <div className="flex justify-between items-center p-4 border-b border-border">
            <h2 className="text-[24px] font-heading">
              {isEditMode ? 'Edit General Contractor' : generalContractor?.name}
            </h2>
            <div className="flex items-center gap-2">
              {!isEditMode && generalContractorAccess.editGeneralContractor && (
                <button
                  id="btn-edit-general-contractor"
                  className="btn-secondary text-small"
                  onClick={handleEditButtonClick}
                  disabled={isLoading}
                >
                  Edit General Contractor
                </button>
              )}
              <button
                id="btn-close-overlay"
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="p-6 space-y-6 pb-24">
                <div className="grid grid-cols-1 gap-4">
                  {/* Always show read-only details, never input fields */}
                  <div>
                    <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Name</label>
                    <div className="text-[16px] font-[500] text-[#000000]">{generalContractor?.name || '-'}</div>
                  </div>

                  <div>
                    <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Status</label>
                    <div>
                      {generalContractor && generalContractor.status ? (
                        <StatusPill status={generalContractor.status.toLowerCase()} />
                      ) : '-'}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[14px] font-[500] text-[#767A81] mb-1">Address</label>
                      {generalContractor?.address ? (
                        <div className="space-y-2">
                          <div className="text-[16px] font-[500] text-[#000000]">
                            {generalContractor.address.address || '-'}
                          </div>
                          <div className="text-[16px] font-[500] text-[#000000]">
                            {generalContractor.address.city ? `${generalContractor.address.city}, ` : ''}{' '}
                            {generalContractor.address.province?.name || ''}
                          </div>
                          <div className="text-[16px] font-[500] text-[#000000]">
                            {generalContractor.address.postalCode || ''}
                          </div>
                        </div>
                      ) : (
                        <div className="text-[16px] font-[500] text-[#000000]">-</div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralContractorDetailsOverlay;